import { forwardRef } from 'react';
import type { KeyboardEvent, MouseEvent } from 'react';

import { Box, type BoxProps } from '@frontend/legacy-components';

export type ViewerButtonProps = Omit<BoxProps, 'as' | 'type'> & {
  /** When `true`, the tool is engaged (toggle on). Omit for non-toggle actions (e.g. reset). */
  isActive?: boolean;
  /** When `true`, the control is inert and not keyboard-focusable (`aria-disabled`). */
  disabled?: boolean;
};

/**
 * Square toolbar control for the 3D viewer. Implemented as `role="button"` on a `div` so
 * global `button { background: none }` (reset.scss) does not strip theme `bg` colors.
 */
const ViewerButton = forwardRef<HTMLDivElement, ViewerButtonProps>(
  (
    {
      isActive,
      children,
      disabled = false,
      onClick,
      onKeyDown,
      sx: sxProp,
      ...rest
    },
    ref,
  ) => {
    const handleKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
      if (disabled) return;
      onKeyDown?.(e);
      if (e.defaultPrevented) return;
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        onClick?.(e as unknown as MouseEvent<HTMLDivElement>);
      }
    };

    const handleClick = (e: MouseEvent<HTMLDivElement>) => {
      if (disabled) return;
      onClick?.(e);
    };

    return (
      <Box
        ref={ref}
        as="div"
        role="button"
        tabIndex={disabled ? -1 : 0}
        aria-disabled={disabled ? true : undefined}
        {...(isActive !== undefined ? { 'aria-pressed': isActive } : {})}
        {...rest}
        display="inline-flex"
        alignItems="center"
        justifyContent="center"
        w={4.5}
        h={4.5}
        p={0}
        border="none"
        borderRadius="0.25rem"
        cursor={disabled ? 'not-allowed' : 'pointer'}
        outline="none"
        transitionProperty="common"
        transitionDuration="fast"
        bg={
          disabled
            ? 'transparent'
            : isActive
            ? 'interaction.active'
            : 'transparent'
        }
        color={
          disabled
            ? 'feature.placeholder'
            : isActive
            ? 'feature.onInteraction'
            : 'feature.secondary'
        }
        opacity={disabled ? 0.55 : 1}
        _hover={
          disabled
            ? { bg: 'transparent' }
            : isActive
            ? { bg: 'interaction.active' }
            : { bg: 'interaction.surfaceHover' }
        }
        _active={
          disabled
            ? undefined
            : { bg: 'interaction.active', color: 'feature.onInteraction' }
        }
        _focusVisible={{
          outline: '2px solid',
          outlineColor: 'interaction.focus',
          outlineOffset: '2px',
        }}
        onClick={handleClick}
        onKeyDown={handleKeyDown}
        sx={sxProp}
      >
        {children}
      </Box>
    );
  },
);

ViewerButton.displayName = 'ViewerButton';

export default ViewerButton;
